import { ShootCalendarControl } from '../calendar/ShootCalendarControl.js';
import { ShootProgress } from './ShootProgress.js';
import { GiftContactsPanel } from './GiftContactsPanel.js';
import { GiftShootPeople } from './GiftShootPeople.js';
import { GiftCardSection } from './GiftCardSection.js';
import { CollapsibleText } from '../../ui/CollapsibleText.js';
import { CopyAction, SaveFeedback, SaveConfirmation } from '../../ui/action-feedback.js';
import { ClientAvatar } from './ClientAvatar.js';
import { useEditingPayment } from './use-editing-payment.js';
import { formatCrmPriceLabel } from './crm-money.js';
import {
  SESSION_STATUS_LABELS,
  type Session,
  type PricingPlanItem,
  type UpdateSessionRequest,
  type DeleteCrmShootRequest,
} from '@diana/contracts';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  Gift,
  ArrowLeft,
  CalendarDays,
  CalendarPlus,
  Check,
  CircleCheck,
  ChevronRight,
  ChevronDown,
  MapPin,
  MessageCircle,
  MoreHorizontal,
  Layers,
  Link2,
  X,
} from 'lucide-react';
import { useId, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useEditorCommandRegistration } from '../../app/EditorCommandContext.js';
import { useAutosave } from '../../lib/use-autosave.js';
import { AdminApiError } from '../../lib/api-client.js';
import { useDianaUIPortal } from '../../ui/index.js';
import { clientContacts, internationalPhone } from './client-contacts.js';
import { formatDateTime, primaryClient, sessionDraft } from './crm-format.js';
import { calendarDaysSinceScheduled } from './shoot-list.js';
import { readShootConflict, type ShootComparison, type ShootDraft } from './shoot-conflict.js';
import { ShootConflictDialog } from './ShootConflictDialog.js';
import {
  ShootDetailsPanel,
  shootMoney,
  type ShootPanelFocus,
  type ShootPanelMode,
} from './ShootDetailsPanel.js';
import { ShootDestructiveDialog } from './ShootDestructiveDialog.js';
import { ShootHistory, formatHistoryDate, type ShootHistoryState } from './ShootHistory.js';
import { packageDurationMinutes } from './package-duration.js';
import { UnsavedChangesGuard } from './UnsavedChangesGuard.js';
import { ShootMoney } from './ShootMoney.js';
import { InlineShootNotes } from './InlineShootNotes.js';
import { InquiryDetails, inquiryRows } from './inquiry-details.js';
import { ClientContactRows } from './ClientContactRows.js';
import { shootCalendar } from './shoot-calendar.js';
import { ShootActionMenuItems } from './ShootActionMenuItems.js';
import './CrmShootEditor.css';
import './crm-detail-sections.css';

type CrmShootEditorProps = {
  shoot: Session;
  save(request: UpdateSessionRequest): Promise<Session>;
  contactsChanged?(shoot: Session): void;
  defaultDurationMinutes?: number;
  packages?: readonly PricingPlanItem[];
  history?: ShootHistoryState;
  deletionPreview?: () => Promise<{
    revision: number;
    client: { id: string; name: string; revision: number; canDelete: boolean };
  }>;
  remove?: (input: DeleteCrmShootRequest) => Promise<void>;
};

export function CrmShootEditor(props: CrmShootEditorProps) {
  const [contactSave, setContactSave] = useState<{
    shoot: Session;
    epoch: number;
    scroll: number;
  } | null>(null);
  const active =
    contactSave?.shoot.id === props.shoot.id && contactSave.shoot.revision >= props.shoot.revision
      ? contactSave.shoot
      : props.shoot;
  return (
    <ShootAggregate
      key={`${props.shoot.id}:${contactSave?.epoch ?? 0}`}
      {...props}
      shoot={active}
      contactRestore={contactSave?.shoot.id === props.shoot.id ? contactSave.scroll : undefined}
      onContactsSaved={(value) => {
        props.contactsChanged?.(value);
        setContactSave((previous) => ({
          shoot: value,
          epoch: (previous?.epoch ?? 0) + 1,
          scroll: window.scrollY,
        }));
      }}
    />
  );
}

function ShootAggregate({
  shoot,
  save,
  deletionPreview,
  remove,
  defaultDurationMinutes,
  packages = [],
  history,
  onContactsSaved,
  contactRestore,
}: CrmShootEditorProps & { onContactsSaved(shoot: Session): void; contactRestore?: number }) {
  // Own-save cache updates arrive before the route's save promise resolves.
  // The mounted aggregate owns its revision until the shoot identity changes.
  const [initialShoot] = useState(shoot);
  const [destructive, setDestructive] = useState<'cancel' | 'delete' | null>(null);
  const attempted = useRef<UpdateSessionRequest | null>(null);
  const conflictMetadata = useRef<(ShootComparison & { shootId: string; revision: number }) | null>(
    null,
  );
  const metadata = useRef({
    revision: shoot.revision,
    clients: new Map(shoot.clients.map((client) => [client.id, client])),
  });
  const rememberClients = (value: Session) => {
    if (value.revision >= metadata.current.revision) {
      value.clients.forEach((client) => metadata.current.clients.set(client.id, client));
      metadata.current.revision = value.revision;
    }
  };
  rememberClients(shoot);
  const savedStatus = useRef(initialShoot.status);
  const editingPayment = useEditingPayment();
  const panelTimeValid = useRef(true);
  const [panelMode, setPanelMode] = useState<ShootPanelMode | null>(null);
  const [panelFocus, setPanelFocus] = useState<ShootPanelFocus>('Shoot title');
  const [allClients, setAllClients] = useState(false);
  const [notesEditing, setNotesEditing] = useState(false);
  const [moneyFormDirty, setMoneyFormDirty] = useState(false);
  const [giftFormDirty, setGiftFormDirty] = useState(false);
  const [giftFormBusy, setGiftFormBusy] = useState(false);
  const [contactsDirty, setContactsDirty] = useState(false);
  const [contactsBusy, setContactsBusy] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const portal = useDianaUIPortal();
  useLayoutEffect(() => {
    window.scrollTo(0, contactRestore ?? 0);
    if (contactRestore !== undefined)
      document
        .querySelector<HTMLButtonElement>(
          '[aria-label="Change recipient"], .gift-shoot-people__add button',
        )
        ?.focus({ preventScroll: true });
  }, [initialShoot.id]);
  const location = useLocation();
  const autosave = useAutosave<ShootDraft>({
    mode: 'manual',
    initialValue: sessionDraft(initialShoot),
    initialRevision: initialShoot.revision,
    save: async ({ value, expectedRevision }) => {
      const request = { ...value, expectedRevision };
      attempted.current = structuredClone(request);
      conflictMetadata.current = null;
      const result = await save(request);
      rememberClients(result);
      savedStatus.current = result.status;
      return { value: sessionDraft(result), revision: result.revision };
    },
    readConflict: (error) => {
      const detected = readShootConflict(error, initialShoot.id, attempted.current);
      if (!detected) return null;
      rememberClients(detected.current);
      conflictMetadata.current = {
        ...detected.comparison,
        shootId: initialShoot.id,
        revision: detected.serverRevision,
      };
      return { serverValue: detected.serverValue, serverRevision: detected.serverRevision };
    },
  });
  const { snapshot } = autosave;
  const draft = snapshot.value;
  const conflicted = snapshot.status === 'Conflict';
  const comparison =
    conflicted &&
    conflictMetadata.current?.shootId === initialShoot.id &&
    conflictMetadata.current.revision === snapshot.conflict?.serverRevision
      ? conflictMetadata.current
      : null;
  const linkedClients = draft.clients.flatMap(({ clientId, ...link }) => {
    const value = metadata.current.clients.get(clientId);
    return value ? [{ ...value, ...link }] : [];
  });
  const giftRoles = shoot.giftContacts ?? initialShoot.giftContacts;
  const client = primaryClient({ clients: linkedClients });
  const buyer = linkedClients.find((person) => person.id === giftRoles?.buyerId) ?? client;
  const contacts = clientContacts(client);
  const international = internationalPhone(client.phone);
  const state: unknown = location.state;
  const originId =
    state && typeof state === 'object' && 'fromClientId' in state ? state.fromClientId : null;
  const origin =
    typeof originId === 'string' ? linkedClients.find((item) => item.id === originId) : null;
  const update = (patch: Partial<ShootDraft>) => autosave.edit({ ...draft, ...patch });
  const commitDraft = async (value?: ShootDraft) => {
    if (value) autosave.edit(value);
    if (autosave.getSnapshot().status === 'Error') await autosave.retry();
    else await autosave.flush();
    const result = autosave.getSnapshot();
    return result.status === 'Saved' && !result.dirty;
  };
  const saveDraft = async (patch?: Partial<ShootDraft>) => {
    if (!panelTimeValid.current) return false;
    const before = autosave.getSnapshot();
    if (before.status === 'Saving') return false;
    const next = { ...before.value, ...patch };
    if (
      savedStatus.current !== next.status &&
      (next.status === 'Editing' || next.status === 'Delivered')
    ) {
      const success = await editingPayment.request(next, commitDraft);
      if (success) {
        setPanelMode(null);
        setNotesEditing(false);
      } else if (autosave.getSnapshot().status !== 'Conflict') {
        autosave.discardChanges();
        if (before.dirty) autosave.edit(before.value);
      }
      return success;
    }
    return commitDraft(patch ? next : undefined);
  };
  const closePanel = () => {
    const current = autosave.getSnapshot();
    if (current.status === 'Saving') {
      if (!current.dirty) setPanelMode(null);
      return;
    }
    if (current.dirty && !window.confirm('Discard unsaved changes?')) return;
    if (current.dirty) autosave.discardChanges();
    setPanelMode(null);
  };
  useEditorCommandRegistration({ status: snapshot.status, save: () => void saveDraft() });
  useLayoutEffect(() => {
    if (conflicted) {
      setPanelMode(null);
      editingPayment.cancel();
    }
  }, [conflicted]);
  const openDetails = (
    focus: ShootPanelFocus = 'Shoot title',
    fromMenu = false,
    mode: ShootPanelMode = 'edit',
  ) => {
    if (notesEditing && autosave.getSnapshot().dirty) {
      if (!window.confirm('Discard unsaved notes?')) return;
      if (!autosave.discardChanges()) return;
    }
    setNotesEditing(false);
    returnFocus.current = fromMenu ? trigger.current : (document.activeElement as HTMLElement);
    setPanelFocus(focus);
    setPanelMode(mode);
  };
  const quickActionHandled = useRef(false);
  useLayoutEffect(() => {
    if (
      !quickActionHandled.current &&
      state &&
      typeof state === 'object' &&
      'quickAction' in state &&
      ['schedule', 'reschedule', 'edit'].includes(String(state.quickAction))
    ) {
      quickActionHandled.current = true;
      const action = state.quickAction;
      openDetails(
        action === 'edit' ? 'Shoot title' : 'Date and time',
        false,
        action === 'reschedule' ? 'reschedule' : action === 'schedule' ? 'schedule' : 'edit',
      );
    }
  }, [state]);
  const restoreFocus = () => {
    const target = returnFocus.current;
    const canRestore =
      target?.isConnected &&
      target.tabIndex >= 0 &&
      !target.matches(':disabled') &&
      !target.closest('[inert], [hidden]') &&
      target.getClientRects().length > 0;
    (canRestore ? target : trigger.current)?.focus();
  };
  const primary =
    draft.status === 'NewRequest'
      ? { label: 'Start discussion', run: () => void saveDraft({ status: 'InDiscussion' }) }
      : draft.status === 'InDiscussion'
        ? { label: 'Schedule shoot', run: () => openDetails('Date and time', false, 'schedule') }
        : draft.status === 'Scheduled'
          ? { label: 'Move to Editing', run: () => void saveDraft({ status: 'Editing' }) }
          : draft.status === 'Editing'
            ? { label: 'Mark delivered', run: () => void saveDraft({ status: 'Delivered' }) }
            : draft.status === 'Canceled'
              ? { label: 'Restore shoot', run: () => openDetails('Status') }
              : null;
  const saveStatus = (
    <SaveFeedback
      status={snapshot.status}
      error={
        snapshot.error instanceof AdminApiError && snapshot.error.kind === 'validation'
          ? snapshot.error.fields?.map((field) => field.message).join(' ') || snapshot.error.message
          : undefined
      }
      onRetry={() => void autosave.retry()}
    />
  );
  const additional = linkedClients.filter((item) => item.id !== client.id);
  const money = (value: number | null) => shootMoney(value, draft.currency);
  const hasDetails = Boolean(
    draft.galleryUrl ||
    (draft.status !== 'NewRequest' && draft.status !== 'InDiscussion' && draft.notes?.trim()) ||
    (draft.status !== 'Scheduled' && draft.scheduledStartAt) ||
    draft.location?.trim() ||
    draft.requestedPackageTitle?.trim() ||
    draft.requestedPackagePriceLabel?.trim() ||
    draft.sourcePath ||
    inquiryRows(shoot.inquiryContext ?? initialShoot.inquiryContext).length,
  );

  return (
    <article className="shoot-page" data-status={draft.status}>
      <UnsavedChangesGuard
        dirty={snapshot.dirty || moneyFormDirty || giftFormDirty || contactsDirty}
        saving={snapshot.status === 'Saving' || giftFormBusy || contactsBusy}
        discard={autosave.discardChanges}
      />
      <header className="shoot-page__heading">
        <Link
          className="shoot-back shoot-back--outline"
          to={origin ? `/crm/clients/${origin.id}` : '/crm/shoots'}
        >
          <ArrowLeft size={20} aria-hidden="true" />
          <span className="shoot-back__label">Back to {origin?.name ?? 'Shoots'}</span>
        </Link>
        <div className="shoot-page__header">
          <div className="shoot-page__identity">
            <div className="shoot-page__title">
              {draft.isGiftCard ? (
                <span className="shoot-gift-title-icon" role="img" aria-label="Gift card">
                  <Gift size={24} aria-hidden="true" />
                </span>
              ) : null}
              <h1>{draft.title}</h1>
            </div>
            <p className="shoot-page__meta">
              {[draft.requestedPackageTitle, formatCrmPriceLabel(draft.requestedPackagePriceLabel)]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
          <nav className="shoot-page__channels" aria-label="Shoot actions">
            <DropdownMenu.Root>
              <DropdownMenu.Trigger
                ref={trigger}
                className="shoot-button shoot-button--icon"
                aria-label="More"
              >
                <MoreHorizontal size={20} aria-hidden="true" />
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal container={portal}>
                <DropdownMenu.Content
                  className="dui-menu shoot-menu"
                  sideOffset={6}
                  align="end"
                  onCloseAutoFocus={(event) => {
                    if (panelMode) event.preventDefault();
                  }}
                >
                  <ShootActionMenuItems
                    status={draft.status}
                    busy={snapshot.status === 'Saving'}
                    canDelete={Boolean(remove) && snapshot.status === 'Saved'}
                    onEdit={() => openDetails('Shoot title', true)}
                    onSchedule={(mode) => openDetails('Date and time', true, mode)}
                    onCancel={() => setDestructive('cancel')}
                    onDelete={() => setDestructive('delete')}
                    onMove={(status) => void saveDraft({ status })}
                  />
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
            {primary ? (
              <div className="shoot-page__next">
                <button
                  className="shoot-button shoot-button--primary"
                  aria-busy={snapshot.status === 'Saving'}
                  disabled={snapshot.status === 'Saving'}
                  onClick={primary.run}
                >
                  {snapshot.status === 'Saving' ? 'Saving…' : primary.label}
                  <ChevronRight size={17} aria-hidden="true" />
                </button>
              </div>
            ) : null}
          </nav>
          <div className="shoot-page__journey">
            <ShootProgress status={draft.status} />
          </div>
        </div>
      </header>
      {!panelMode ? saveStatus : null}
      <div className="shoot-page__layout crm-detail-layout">
        <div className="shoot-page__main-column">
          <section className="shoot-accent" aria-label="Stage summary">
            <StageSummary
              shootId={shoot.id}
              revision={snapshot.revision}
              draft={draft}
              history={history}
            />
          </section>
          <div className="shoot-page__main">
            {hasDetails ? (
              <section
                className="shoot-details crm-detail-section"
                aria-labelledby="shoot-details-title"
              >
                <h2 id="shoot-details-title" className="crm-section-title">
                  Details
                </h2>
                <dl>
                  {draft.galleryUrl ? (
                    <div className="shoot-detail">
                      <Link2 size={19} aria-hidden="true" />
                      <dt>Gallery</dt>
                      <dd>
                        <a href={draft.galleryUrl} target="_blank" rel="noopener noreferrer">
                          Open gallery
                        </a>
                      </dd>
                    </div>
                  ) : null}
                  {draft.status !== 'Scheduled' && draft.scheduledStartAt ? (
                    <div className="shoot-detail">
                      <CalendarDays size={19} aria-hidden="true" />
                      <dt>When</dt>
                      <dd>{formatDateTime(draft.scheduledStartAt)}</dd>
                    </div>
                  ) : null}
                  {draft.location?.trim() ? (
                    <div className="shoot-detail">
                      <MapPin size={19} aria-hidden="true" />
                      <dt>Place</dt>
                      <dd>{draft.location}</dd>
                    </div>
                  ) : null}
                  {draft.requestedPackageTitle?.trim() ||
                  draft.requestedPackagePriceLabel?.trim() ? (
                    <div className="shoot-detail shoot-detail--package">
                      <Layers size={19} aria-hidden="true" />
                      <dt>Package</dt>
                      <dd>
                        {draft.requestedPackageTitle}
                        {draft.requestedPackageTitle && draft.requestedPackagePriceLabel
                          ? ' · '
                          : null}
                        {draft.requestedPackagePriceLabel ? (
                          <small>{formatCrmPriceLabel(draft.requestedPackagePriceLabel)}</small>
                        ) : null}
                      </dd>
                    </div>
                  ) : null}
                  <InquiryDetails
                    sourcePath={draft.sourcePath}
                    context={shoot.inquiryContext ?? initialShoot.inquiryContext}
                  >
                    {draft.status !== 'NewRequest' &&
                    draft.status !== 'InDiscussion' &&
                    draft.notes?.trim() ? (
                      <ClientMessageDetail message={draft.notes} />
                    ) : null}
                  </InquiryDetails>
                </dl>
              </section>
            ) : null}
            {draft.isGiftCard ? (
              <GiftCardSection
                shootId={shoot.id}
                shootRevision={snapshot.revision}
                packageTitle={draft.requestedPackageTitle ?? null}
                onDirtyChange={setGiftFormDirty}
                onBusyChange={setGiftFormBusy}
              />
            ) : null}
            <InlineShootNotes
              value={draft.internalNotes || ''}
              editing={notesEditing}
              busy={snapshot.status === 'Saving'}
              disabled={conflicted}
              dirty={snapshot.dirty}
              onEdit={() => setNotesEditing(true)}
              onChange={(value) => update({ internalNotes: value || null })}
              onSave={async () => {
                if (await saveDraft()) setNotesEditing(false);
              }}
              onCancel={() => {
                if (autosave.discardChanges()) setNotesEditing(false);
              }}
            />
          </div>
          <ShootHistory createdAt={initialShoot.createdAt} history={history} />
        </div>
        <aside className="shoot-page__rail">
          <section className="shoot-client crm-detail-section" aria-labelledby="shoot-client-title">
            <h2 id="shoot-client-title" className="crm-section-title">
              {draft.isGiftCard ? 'Clients' : 'Client'}
            </h2>
            {draft.isGiftCard && giftRoles ? (
              <GiftShootPeople
                shoot={{
                  ...shoot,
                  ...draft,
                  revision: snapshot.revision,
                  clients: linkedClients,
                  giftContacts: giftRoles,
                }}
                action={
                  <GiftContactsPanel
                    shoot={{
                      ...shoot,
                      status: draft.status,
                      revision: snapshot.revision,
                      clients: linkedClients,
                    }}
                    disabled={
                      snapshot.dirty ||
                      snapshot.status === 'Saving' ||
                      conflicted ||
                      giftFormDirty ||
                      giftFormBusy ||
                      moneyFormDirty
                    }
                    onSaved={onContactsSaved}
                    onDirtyChange={setContactsDirty}
                    onBusyChange={setContactsBusy}
                  />
                }
              />
            ) : (
              <>
                <div className="shoot-client__heading">
                  <ClientAvatar
                    id={client.id}
                    name={client.name}
                    className="shoot-client__avatar"
                  />
                  <Link to={`/crm/clients/${client.id}`}>
                    {client.name}
                    <ChevronRight size={16} aria-hidden="true" />
                  </Link>
                  {additional.length ? (
                    <button
                      className="shoot-button shoot-button--outline shoot-button--more"
                      aria-expanded={allClients}
                      onClick={() => setAllClients(!allClients)}
                    >
                      +{additional.length} more
                    </button>
                  ) : null}
                </div>
                {allClients ? (
                  <ul className="shoot-client__others">
                    {additional.map((item) => (
                      <li key={item.id}>
                        <Link to={`/crm/clients/${item.id}`}>
                          {item.name}
                          <ChevronRight size={15} aria-hidden="true" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : null}
                <ClientContactRows contacts={contacts} international={international} />
                {!contacts.length ? <p className="shoot-page__muted">No contact details</p> : null}
              </>
            )}
          </section>
          <ShootMoney
            price={draft.priceAmountMinor}
            currency={draft.currency}
            items={draft.transactions ?? []}
            busy={snapshot.status === 'Saving'}
            disabled={conflicted}
            onDirtyChange={setMoneyFormDirty}
            onReset={() => {
              autosave.discardChanges();
            }}
            onBeforeOpen={() => {
              if (snapshot.dirty) {
                if (!window.confirm('Discard unsaved changes before recording a transaction?'))
                  return false;
                if (!autosave.discardChanges()) return false;
              }
              setNotesEditing(false);
              return true;
            }}
            onSave={async (transactions) =>
              saveDraft({
                transactions,
                expensesAmountMinor: transactions
                  .filter((item) => item.kind === 'Expense')
                  .reduce((sum, item) => sum + item.amountMinor, 0),
              })
            }
          />
        </aside>
      </div>
      <SaveConfirmation
        status={snapshot.status}
        revision={snapshot.revision}
        dirty={snapshot.dirty}
      />
      {destructive ? (
        <ShootDestructiveDialog
          mode={destructive}
          name={client.name}
          revision={snapshot.revision}
          preview={deletionPreview}
          remove={remove}
          close={() => {
            setDestructive(null);
            trigger.current?.focus();
          }}
          cancel={async () => {
            update({ status: 'Canceled' });
            if (autosave.getSnapshot().status === 'Error') await autosave.retry();
            else await autosave.flush();
            const result = autosave.getSnapshot();
            if (result.status !== 'Saved' || result.value.status !== 'Canceled')
              throw new Error('Cancellation was not saved');
          }}
        />
      ) : null}
      <ShootDetailsPanel
        mode={panelMode}
        focus={panelFocus}
        draft={draft}
        clientName={client.name}
        additionalClients={additional.map((item) => item.name)}
        packages={packages}
        defaultDurationMinutes={
          !draft.requestedPackageKey
            ? 60
            : packages.find((plan) => plan.key === draft.requestedPackageKey)
              ? packageDurationMinutes(
                  packages.find((plan) => plan.key === draft.requestedPackageKey)?.payload,
                )
              : defaultDurationMinutes
        }
        saveStatus={saveStatus}
        saveError={snapshot.status === 'Error'}
        conflicted={conflicted}
        onChange={update}
        onClose={closePanel}
        onSave={saveDraft}
        onValidityChange={(valid) => {
          panelTimeValid.current = valid;
        }}
        dirty={snapshot.dirty || moneyFormDirty || giftFormDirty || contactsDirty}
        saving={snapshot.status === 'Saving'}
        onRestoreFocus={restoreFocus}
      />
      {editingPayment.dialog}
      <ShootConflictDialog
        open={conflicted && !panelMode}
        comparison={comparison}
        latest={draft}
        clientNames={new Map([...metadata.current.clients].map(([id, value]) => [id, value.name]))}
        onRestoreFocus={restoreFocus}
        onAcceptServer={() => {
          if (comparison) savedStatus.current = comparison.current.status;
          autosave.acceptServerVersion();
        }}
        onReapplyLocal={() => {
          autosave.reapplyLocal();
          void autosave.flush();
        }}
        onCopyLocal={async () => {
          await navigator.clipboard.writeText(JSON.stringify(autosave.copyLocal(), null, 2));
        }}
      />
    </article>
  );
}

function ClientMessageDetail({ message }: { message: string }) {
  const messageId = useId();
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="shoot-detail shoot-detail--message">
      <MessageCircle size={19} aria-hidden="true" />
      <dt>Message</dt>
      <dd>
        <button
          type="button"
          className="shoot-client-message-detail"
          aria-expanded={expanded}
          aria-controls={messageId}
          onClick={() => setExpanded((value) => !value)}
          title={expanded ? 'Collapse client message' : 'Expand client message'}
        >
          <CollapsibleText id={messageId} expanded={expanded} lines={1}>
            {message}
          </CollapsibleText>
          <ChevronDown size={16} aria-hidden="true" />
        </button>
      </dd>
    </div>
  );
}

function StageSummary({
  shootId,
  revision,
  draft,
  history,
}: {
  shootId: string;
  revision: number;
  draft: ShootDraft;
  history?: ShootHistoryState;
}) {
  const [expanded, setExpanded] = useState(false);
  const [longMessage, setLongMessage] = useState(false);
  if (draft.status === 'NewRequest' || draft.status === 'InDiscussion')
    return (
      <>
        <div className="shoot-accent__heading">
          <h2>Client message</h2>
        </div>
        <CollapsibleText
          id="shoot-client-message"
          className="shoot-accent__message"
          expanded={expanded}
          lines={4}
          onOverflowChange={setLongMessage}
        >
          {draft.notes || 'No message yet.'}
        </CollapsibleText>
        {longMessage || expanded ? (
          <button
            className="shoot-button shoot-button--text shoot-message-toggle"
            aria-expanded={expanded}
            aria-controls="shoot-client-message"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Show less' : 'Read full message'}
          </button>
        ) : null}
      </>
    );
  if (draft.status === 'Scheduled') {
    const calendar = shootCalendar(shootId, draft);
    const start = draft.scheduledStartAt ? new Date(draft.scheduledStartAt) : null;
    const end = draft.scheduledEndAt ? new Date(draft.scheduledEndAt) : null;
    const time = (value: Date) =>
      new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' }).format(value);
    return start ? (
      <div className="shoot-accent__date shoot-accent__date--scheduled">
        <span>
          <small>{new Intl.DateTimeFormat('en-GB', { month: 'short' }).format(start)}</small>
          <strong>{start.getDate()}</strong>
        </span>
        <div>
          <p>
            {new Intl.DateTimeFormat('en-GB', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            }).format(start)}
          </p>
          <strong>
            {time(start)}
            {end ? ` – ${time(end)}` : ''}
          </strong>
        </div>
        <ShootCalendarControl
          id={shootId}
          revision={revision}
          fallback={
            calendar ? (
              <a
                className="shoot-accent__calendar"
                href={`data:text/calendar;charset=utf-8,${encodeURIComponent(calendar)}`}
                download={`shoot-${shootId}.ics`}
                aria-label="Add to calendar"
                title="Add to calendar"
              >
                <CalendarPlus size={20} aria-hidden="true" />
              </a>
            ) : null
          }
        />
      </div>
    ) : (
      <>
        <h2>Scheduled</h2>
        <p>Date and time: Not set</p>
      </>
    );
  }
  if (draft.status === 'Editing') {
    const days = calendarDaysSinceScheduled(draft.scheduledStartAt, Date.now());
    return (
      <div className="shoot-accent__date">
        {days !== undefined ? (
          <span>
            <strong>{days}</strong>
            <small>DAYS</small>
          </span>
        ) : null}
        <div>
          <h2>In editing</h2>
          <p>
            {draft.scheduledStartAt
              ? `Since shoot date · ${formatHistoryDate(draft.scheduledStartAt)}`
              : 'Shoot date not set'}
          </p>
        </div>
      </div>
    );
  }
  if (draft.status === 'Delivered' && !import.meta.env.DEV) {
    return (
      <div className="shoot-accent__terminal">
        <span aria-hidden="true">
          <Check size={24} />
        </span>
        <h2>Delivered</h2>
      </div>
    );
  }
  if (draft.status === 'Delivered') {
    const deliveredAt = history?.entries
      .filter((entry) => entry.toStatus === 'Delivered')
      .sort((a, b) => Date.parse(b.changedAt) - Date.parse(a.changedAt))[0]?.changedAt;
    return (
      <div className="shoot-accent__date shoot-delivery-summary">
        <span className="shoot-delivery-summary__icon" aria-hidden="true">
          <CircleCheck size={42} strokeWidth={1.8} />
        </span>
        <div className="shoot-delivery-summary__content">
          <div className="shoot-delivery-summary__header">
            <h2>{import.meta.env.DEV ? 'Client page' : 'Delivered'}</h2>
            <span className="shoot-delivery-summary__date">
              {history?.pending ? (
                'Loading…'
              ) : deliveredAt ? (
                <>
                  <span className="shoot-delivery-summary__date-label">Delivered · </span>
                  <time
                    dateTime={deliveredAt}
                    aria-label={`Delivered ${formatHistoryDate(deliveredAt)}`}
                  >
                    {formatHistoryDate(deliveredAt)}
                  </time>
                </>
              ) : history?.error ? (
                'Date unavailable'
              ) : (
                'Delivered'
              )}
            </span>
          </div>
          {import.meta.env.DEV ? (
            <>
              <div className="shoot-delivery-summary__link">
                <div className="shoot-delivery-summary__text">
                  <a
                    href="https://perova.photo/s/a7k2m9"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="https://perova.photo/s/a7k2m9"
                  >
                    <Link2
                      className="shoot-delivery-summary__link-icon"
                      size={16}
                      aria-hidden="true"
                    />
                    <span>perova.photo/s/a7k2m9</span>
                  </a>
                </div>
                <CopyAction label="Client link" value="https://perova.photo/s/a7k2m9" />
              </div>
            </>
          ) : null}
        </div>
      </div>
    );
  }
  return (
    <div className="shoot-accent__terminal">
      <span aria-hidden="true">
        <X size={24} />
      </span>
      <h2>{SESSION_STATUS_LABELS[draft.status]}</h2>
    </div>
  );
}
